import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import AdminLogin from './components/AdminLogin';
import UserLogin from './components/UserLogin';
import AdminDashboard from './components/AdminDashboard';
import UserDashboard from './components/UserDashboard';
import ChangePassword from './components/ChangePassword';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import './App.css';
import AdminChangePassword from './components/AdminChangePassword';

export const API = "https://caceres-reservas.aip-3.com/api";

// ✅ Check-in por QR: captura "?checkin=1&code=XYZ" de la URL apenas carga la
// app —a nivel de módulo, antes de que React Router decida a dónde navegar—
// para que no se pierda si el docente todavía no había iniciado sesión.
// Sin esto, si el docente escanea el QR sin estar logueado, la ruta
// "/user-dashboard" lo redirige a "/user-login" con <Navigate>, que descarta
// el query string, y UserDashboard nunca llega a montarse para guardarlo:
// el check-in automático se perdía y no aparecía nada tras iniciar sesión.
// UserDashboard revisa este valor en sessionStorage al montar y dispara el
// check-in automáticamente (no hay botón manual: la asistencia solo se
// registra escaneando el código físico del AIP).
(function capturePendingCheckin() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkin") === "1") {
      sessionStorage.setItem("pending_checkin", "1");
      const qrCode = params.get("code");
      if (qrCode) {
        sessionStorage.setItem("pending_checkin_code", qrCode);
      }
    }
  } catch (e) {
    console.error("Error al capturar el check-in pendiente:", e);
  }
})();

// 🔐 "Mantener sesión iniciada": los logins guardan 'rememberSession' = '1' o '0'.
// Si NO se marcó, la sesión solo vive mientras el navegador siga abierto: al abrirlo
// de nuevo (sessionStorage vacío) se limpia la sesión guardada. Las sesiones antiguas
// (sin esa marca) y las marcadas con "Mantener" NO se tocan.
(function enforceSessionPolicy() {
  try {
    const remember = localStorage.getItem('rememberSession');
    const alive = sessionStorage.getItem('session_alive');
    if (localStorage.getItem('token') && remember === '0' && !alive) {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('userData');
      localStorage.removeItem('rememberSession');
    }
  } catch (e) {
    console.error('Error al validar la política de sesión:', e);
  }
})();

// 🔌 Deriva la URL del WebSocket a partir de la URL del API (http->ws, https->wss)
const getWsUrl = () => {
  try {
    const httpBase = API.replace(/\/api\/?$/, '');
    return httpBase.replace(/^http/, 'ws') + '/ws/reservations';
  } catch {
    return null;
  }
};
    
// --- PANTALLA CUANDO EL SISTEMA ESTÁ INACTIVO (solo mensaje arriba) ---
const MensajeInactivo = ({ motivo }) => {
  return (
    <div className="w-full max-w-4xl mx-auto bg-gradient-to-r from-red-800 to-red-600 text-white py-8 px-10 rounded-2xl shadow-2xl mb-12 border border-red-400/30">
      <div className="flex items-center justify-center gap-6 mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h2 className="text-3xl md:text-4xl font-bold drop-shadow-lg">
          Sistema de Reservas Temporalmente Desactivado
        </h2>
      </div>

      {motivo ? (
        <div className="bg-white/15 rounded-xl p-6 mb-8 text-lg md:text-xl">
          <p className="font-medium">Motivo:</p>
          <p className="italic mt-2">{motivo}</p>
        </div>
      ) : (
        <p className="text-xl md:text-2xl mb-8 opacity-90">
          El administrador ha desactivado temporalmente el sistema de reservas.
        </p>
      )}

      <p className="text-lg opacity-80">
        Los docentes y directivos no pueden realizar reservas en este momento.
      </p>
    </div>
  );
};

// --- HOME (pantalla inicial) ---
const Home = () => {
  const navigate = useNavigate();
  const [isEnabled, setIsEnabled] = useState(true);
  const [motivo, setMotivo] = useState("");
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const resStatus = await axios.get(`${API}/system/reservations-status`);
        setIsEnabled(resStatus.data.enabled);

        if (!resStatus.data.enabled) {
          const resMotivo = await axios.get(`${API}/system/reservations-motivo`);
          setMotivo(resMotivo.data.motivo || "");
        }
      } catch (error) {
        console.error("Error al verificar estado:", error);
        setIsEnabled(true); // Por defecto activo si falla
      } finally {
        setLoadingStatus(false);
      }
    };
    checkStatus();
  }, []);

  // 🔌 Sincronización en tiempo real: sin esto, si el admin bloquea/desbloquea
  // el sistema mientras un docente está en esta pantalla (por ejemplo, justo
  // después de cerrar sesión), el mensaje de "Sistema Desactivado" quedaba
  // desactualizado hasta refrescar la página manualmente.
  const wsRef = useRef(null);
  useEffect(() => {
    let reconnectTimer = null;
    let closedByUs = false;

    const connect = () => {
      const wsUrl = getWsUrl();
      if (!wsUrl) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (evt) => {
        let msg;
        try {
          msg = JSON.parse(evt.data);
        } catch {
          return;
        }
        if (msg.event === 'config_updated') {
          if (typeof msg.enabled === 'boolean') {
            setIsEnabled(msg.enabled);
          }
          if (typeof msg.motivo === 'string') {
            setMotivo(msg.motivo);
          }
        }
      };

      ws.onclose = () => {
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS home] error de conexión:', err);
        ws.close();
      };
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');

    if (token && userType) {
      if (userType === 'admin') navigate('/admin-dashboard');
      else if (userType === 'user') navigate('/user-dashboard');
    }
  }, [navigate]);

  if (loadingStatus) {
    return (
      <div className="min-h-screen bg-[#2d5a43] flex items-center justify-center">
        <p className="text-white text-xl">Verificando estado del sistema...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2d5a43] relative flex flex-col items-center p-0 transition-colors duration-500">
      {/* FRANJA SUPERIOR */}
      <div className="w-full bg-[#1a3a2a] py-4 shadow-lg border-b border-white/10 z-10">
        {/* ... tu franja superior ... */}
        <div className="container mx-auto px-4 flex justify-center items-center">
          <p className="text-white text-sm md:text-xl tracking-[0.3em] uppercase font-bold opacity-95 drop-shadow-md text-center">
            I.E.E "MARISCAL ANDRÉS AVELINO CÁCERES" - TUMBES
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center w-full p-6">
        {/* LOGO */}
        <div className="mb-8 mt-4">
          <img
            src="/logo_aip.png"
            alt="Logo AIP"
            className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl"
          />
        </div>

        <div className="text-center mb-10">
          <h1 className="text-white text-4xl md:text-6xl font-serif font-bold mb-3 drop-shadow-md">
            Sistema de Reservas AIP
          </h1>
          <p className="text-gray-200 text-lg italic opacity-80">
            Aula de Innovación Pedagógica - MAAC - TUMBES
          </p>
        </div>

        {/* Mensaje de inactivo (si aplica) */}
        {!isEnabled && <MensajeInactivo motivo={motivo} />}

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 w-full max-w-5xl px-4">
          {/* TARJETA DOCENTE - solo visible si activo */}
          {isEnabled && (
            <div className="bg-[#fdf6e3] rounded-[40px] p-8 flex flex-col items-center shadow-[0_20px_50px_rgba(0,0,0,0.4)] border-4 border-[#e6ccb2] transform transition-all hover:scale-105 w-full md:w-[45%] max-w-sm">
              <div className="bg-[#2d5a43] p-4 rounded-full mb-4 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-[#2d5a43] text-2xl font-extrabold mb-1">Docentes / Directivos</h2>
              <p className="text-[#2d5a43] font-medium opacity-70 mb-6 text-center">Clic para reservas del AIP</p>
              <button
                onClick={() => navigate('/user-login')}
                className="w-full bg-[#2d5a43] text-white py-4 rounded-2xl font-black text-lg hover:bg-[#3a7a5a] shadow-lg transition-all animate-pulse uppercase tracking-widest border-2 border-[#fdf6e3]/20"
              >
                Ingresar ahora
              </button>
            </div>
          )}

          {/* TARJETA ADMINISTRADOR - siempre visible */}
          <div className={`bg-white/5 backdrop-blur-md rounded-[30px] p-6 flex flex-col items-center border border-white/10 w-full ${isEnabled ? 'md:w-1/3' : 'md:w-1/2'} opacity-80 hover:opacity-100 transition-opacity`}>
            <h3 className="text-white/90 text-xl font-bold mb-4">Administración</h3>
            <p className="text-white/70 text-center mb-6">
              {isEnabled 
                ? "Acceso exclusivo para gestión del sistema" 
                : "Único acceso disponible (para activar el sistema)"}
            </p>
            <button
              onClick={() => navigate('/admin-login')}
              className="bg-[#2d5a43] text-white py-3 px-10 rounded-xl font-bold text-lg hover:bg-[#3a7a5a] shadow-lg transition-all"
            >
              Ingresar al panel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- APP PRINCIPAL ---
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const type = localStorage.getItem('userType');

    if (token && type) {
      setIsAuthenticated(true);
      setUserType(type);
    }
  }, []);

  return (
    <Router>
      <div className="App relative min-h-screen">
        <div className="fixed bottom-4 right-6 pointer-events-none z-[9999] select-none">
          <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-widest uppercase">
            © 2026 | Cristian. E. Garcia Carrasco.
          </p>
        </div>

        <Toaster position="top-center" richColors />

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/user-login" element={<UserLogin setAuth={setIsAuthenticated} setUserType={setUserType} />} />
          <Route path="/admin-login" element={<AdminLogin setAuth={setIsAuthenticated} setUserType={setUserType} />} />
          <Route path="/change-password" element={isAuthenticated && userType === 'user' ? <ChangePassword setAuth={setIsAuthenticated} setUserType={setUserType} /> : <Navigate to="/user-login" replace />} />
          <Route path="/admin-dashboard" element={isAuthenticated && userType === 'admin' ? <AdminDashboard /> : <Navigate to="/admin-login" replace />} />
          <Route path="/user-dashboard" element={isAuthenticated && userType === 'user' ? <UserDashboard /> : <Navigate to="/user-login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route path="/admin-change-password" element={<AdminChangePassword />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;